// /tickets/:id — Linear-style chat thread + composer + status & assignee
// controls. Two columns:
//   • Left  — thread (chronological messages, customer left / staff
//             right) + composer pinned to the bottom.
//   • Right — tenant context card (link to tenant detail), status
//             dropdown, assignee dropdown (Mine / Unassigned for now;
//             P6 ships a real staff picker once /admin/v1/staff lands).

import { useState, type FormEvent, type KeyboardEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, MessageSquare, Send, User } from 'lucide-react';
import { toast } from 'sonner';
import { useApiClient, useStaffMe } from '../../lib/providers.js';
import {
  TICKETS_LIST_QUERY_KEY,
  ticketQueryKey,
  ticketsApi,
  statusBadgeVariant,
  statusLabel,
  type TicketDetail,
  type TicketMessage,
  type TicketStatus,
} from '../../lib/api/tickets.js';
import { Badge } from '../../components/ui/badge.js';
import { Button } from '../../components/ui/button.js';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card.js';
import { Label } from '../../components/ui/label.js';
import { Select } from '../../components/ui/select.js';
import { Textarea } from '../../components/ui/textarea.js';
import { Skeleton } from '../../components/ui/skeleton.js';
import { ErrorRetry } from '../../components/ui/error-retry.js';
import { formatDate, formatDateTime } from '../../lib/format-date.js';

const STATUSES: TicketStatus[] = [
  'open',
  'in_progress',
  'waiting_customer',
  'resolved',
  'closed',
];

export function TicketDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const client = useApiClient();
  const query = useQuery({
    queryKey: ticketQueryKey(id),
    queryFn: () => ticketsApi.detail(client, id),
    enabled: id.length > 0,
  });

  return (
    <div className="space-y-6">
      <Link
        to="/tickets"
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft aria-hidden className="h-3 w-3" />
        Tickets
      </Link>

      {query.isPending ? (
        <DetailSkeleton />
      ) : query.isError ? (
        <ErrorRetry
          title="Failed to load ticket"
          error={query.error}
          onRetry={() => {
            void query.refetch();
          }}
        />
      ) : (
        <DetailGrid ticket={query.data} />
      )}
    </div>
  );
}

function DetailGrid({ ticket }: { ticket: TicketDetail }) {
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <div className="space-y-4 lg:col-span-2">
        <TicketHeader ticket={ticket} />
        <Thread messages={ticket.messages} />
        <Composer ticketId={ticket.id} status={ticket.status} />
      </div>
      <div className="space-y-4 lg:col-span-1">
        <ContextCard ticket={ticket} />
        <ControlsCard ticket={ticket} />
      </div>
    </div>
  );
}

function TicketHeader({ ticket }: { ticket: TicketDetail }) {
  return (
    <div>
      <h1 className="text-2xl font-semibold tracking-tight">{ticket.subject}</h1>
      <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
        <Badge variant={statusBadgeVariant(ticket.status)}>{statusLabel(ticket.status)}</Badge>
        <Badge variant="outline">priority: {ticket.priority}</Badge>
        <span>·</span>
        <span>opened {formatDate(ticket.createdAt)}</span>
        <span>·</span>
        <span>{ticket.messages.length} messages</span>
      </div>
    </div>
  );
}

function Thread({ messages }: { messages: readonly TicketMessage[] }) {
  if (messages.length === 0) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          No messages yet.
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="space-y-3">
      {messages.map((m) => (
        <ThreadMessage key={m.id} message={m} />
      ))}
    </div>
  );
}

function ThreadMessage({ message }: { message: TicketMessage }) {
  const isStaff = message.authorKind === 'staff';
  return (
    <div className={`flex gap-3 ${isStaff ? 'flex-row-reverse' : 'flex-row'}`}>
      <div
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
          isStaff ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
        }`}
        aria-hidden
      >
        {isStaff ? <MessageSquare className="h-4 w-4" /> : <User className="h-4 w-4" />}
      </div>
      <div
        className={`flex-1 space-y-1 rounded-lg border px-4 py-3 text-sm ${
          isStaff ? 'border-primary/40 bg-primary/5' : 'border-border bg-card'
        }`}
      >
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="font-medium">
            {isStaff ? 'Staff' : 'Customer'}
            {message.authorId ? ` · ${message.authorId}` : ''}
          </span>
          <span>·</span>
          <span>{formatDateTime(message.createdAt)}</span>
        </div>
        <div className="whitespace-pre-wrap leading-relaxed">{message.body}</div>
      </div>
    </div>
  );
}

function Composer({ ticketId, status }: { ticketId: string; status: TicketStatus }) {
  const client = useApiClient();
  const qc = useQueryClient();
  const [body, setBody] = useState('');

  const mut = useMutation({
    mutationFn: (text: string) => ticketsApi.reply(client, ticketId, text),
    onSuccess: () => {
      toast.success('Reply sent');
      setBody('');
      void qc.invalidateQueries({ queryKey: ticketQueryKey(ticketId) });
      void qc.invalidateQueries({ queryKey: TICKETS_LIST_QUERY_KEY });
    },
    onError: (err: { message?: string }) => {
      toast.error(err.message ?? 'Reply failed');
    },
  });

  function submit(e: FormEvent): void {
    e.preventDefault();
    const trimmed = body.trim();
    if (trimmed.length === 0 || mut.isPending) return;
    mut.mutate(trimmed);
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>): void {
    // Cmd/Ctrl+Enter sends — matches Linear / Slack composer convention.
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      const trimmed = body.trim();
      if (trimmed.length === 0 || mut.isPending) return;
      mut.mutate(trimmed);
    }
  }

  if (status === 'closed') {
    return (
      <Card>
        <CardContent className="py-6 text-center text-sm text-muted-foreground">
          This ticket is closed. Customer must file a new ticket; staff can
          reopen via the status control on the right if needed.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">Reply</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-3">
          <Textarea
            value={body}
            onChange={(e) => {
              setBody(e.target.value);
            }}
            onKeyDown={onKeyDown}
            rows={5}
            placeholder="Type your reply… Cmd/Ctrl+Enter to send."
            disabled={mut.isPending}
          />
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{body.length} chars · 10000 max</span>
            <Button
              type="submit"
              size="sm"
              disabled={body.trim().length === 0 || mut.isPending}
            >
              <Send aria-hidden className="mr-1 h-3.5 w-3.5" />
              {mut.isPending ? 'Sending…' : 'Send reply'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function ContextCard({ ticket }: { ticket: TicketDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Tenant context</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <Row
          label="Tenant"
          value={
            <Link to={`/tenants/${ticket.tenantId}`} className="hover:text-foreground">
              {ticket.tenantName}
            </Link>
          }
        />
        <Row
          label="Plan"
          value={
            ticket.tenantPlanCode ? (
              <Badge variant="secondary">{ticket.tenantPlanCode}</Badge>
            ) : (
              '—'
            )
          }
        />
        <Row
          label="Plan at filing"
          value={<span className="text-muted-foreground">{ticket.planCodeAtCreation}</span>}
        />
        {ticket.tenantFrozenAt && (
          <Row
            label="Frozen"
            value={<Badge variant="destructive">since {formatDate(ticket.tenantFrozenAt)}</Badge>}
          />
        )}
        <Row label="Filed by" value={<code className="text-xs">{ticket.createdBy}</code>} />
      </CardContent>
    </Card>
  );
}

function ControlsCard({ ticket }: { ticket: TicketDetail }) {
  const client = useApiClient();
  const qc = useQueryClient();
  const me = useStaffMe();

  const patch = useMutation({
    mutationFn: (input: { status?: TicketStatus; assignedStaffId?: string | null }) =>
      ticketsApi.patch(client, ticket.id, input),
    onSuccess: () => {
      toast.success('Ticket updated');
      void qc.invalidateQueries({ queryKey: ticketQueryKey(ticket.id) });
      void qc.invalidateQueries({ queryKey: TICKETS_LIST_QUERY_KEY });
    },
    onError: (err: { message?: string }) => {
      toast.error(err.message ?? 'Update failed');
    },
  });

  const assignedToMe = ticket.assignedStaffId === me.staffId;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Controls</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Status</Label>
          <Select
            value={ticket.status}
            disabled={patch.isPending}
            onChange={(e) => {
              patch.mutate({ status: e.target.value as TicketStatus });
            }}
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {statusLabel(s)}
              </option>
            ))}
          </Select>
        </div>

        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">Assignee</Label>
          <div className="flex flex-col gap-2">
            <div className="text-sm">
              {ticket.assignedStaffId ? (
                <code className="text-xs">{ticket.assignedStaffId}</code>
              ) : (
                <span className="italic text-muted-foreground">unassigned</span>
              )}
            </div>
            <div className="flex gap-2">
              {!assignedToMe && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={patch.isPending}
                  onClick={() => {
                    patch.mutate({ assignedStaffId: me.staffId });
                  }}
                >
                  Assign to me
                </Button>
              )}
              {ticket.assignedStaffId && (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={patch.isPending}
                  onClick={() => {
                    patch.mutate({ assignedStaffId: null });
                  }}
                >
                  Unassign
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              A staff picker (assign to anyone on the team) lands once
              <code> /admin/v1/staff</code> ships in P6. For now: take or release.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <div className="text-right">{value}</div>
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <div className="space-y-4 lg:col-span-2">
        <Skeleton className="h-12 w-2/3" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
      <div className="space-y-4 lg:col-span-1">
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    </div>
  );
}
